










































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ComparisonGroup, Company, Brand, Product } from "@/models";
import EntrySearch, { EntrySearchResult } from "@/components/EntrySearch.vue";
import EntryList from "@/components/EntryList.vue";
import EntryListGroup from "@/components/EntryListGroup.vue";
import ComparisonGroupsMixin from "@/mixins/http/ComparisonGroups";

const STATUS_DEFAULT = "default";
const STATUS_CREATE = "create";
const STATUS_EDIT = "edit";

const MAX_ELEMENTS_COUNT = 10;

const REQUEST_TAG = "comparison_group";

@Component({
  components: {
    EntrySearch,
    EntryList,
    EntryListGroup,
  },
})
export default class ComparisonGroupEditor extends Mixins(
  ComparisonGroupsMixin
) {
  @Prop({ default: MAX_ELEMENTS_COUNT }) readonly maxElements?: number;

  status: string = STATUS_DEFAULT;
  loading = false;
  current: ComparisonGroup = new ComparisonGroup({});

  selected: ComparisonGroup | null = null;

  groups: ComparisonGroup[] = [];

  searchResult: EntrySearchResult = {
    companies: [],
    brands: [],
    products: [],
    total: 0,
  };

  get isCreating(): boolean {
    return this.status === STATUS_CREATE;
  }

  get isEditing(): boolean {
    return this.status === STATUS_EDIT;
  }

  get isSelected(): boolean {
    return this.selected !== null;
  }

  get canAddElements(): boolean {
    if (!this.selected) {
      return false;
    }

    return (
      this.selected.elementsCount < (this.maxElements || MAX_ELEMENTS_COUNT)
    );
  }

  created() {
    this.load();
  }

  startCreating() {
    this.status = STATUS_CREATE;

    this.$nextTick(() => {
      (this.$refs.name as HTMLInputElement).focus();
    });
  }

  stopCreating() {
    this.status = STATUS_DEFAULT;
    this.current = new ComparisonGroup({});
  }

  startEditing() {
    this.status = STATUS_EDIT;

    this.$nextTick(() => {
      (this.$refs.search as EntrySearch).focus();
    });
  }

  stopEditing() {
    this.status = STATUS_DEFAULT;
    this.clearSearchResult();
  }

  select(group: ComparisonGroup) {
    if (this.selected === group) {
      return;
    }

    this.status = STATUS_DEFAULT;
    this.selected = group;

    this.emitSelected();
  }

  public selectFirst() {
    if (this.groups.length) {
      this.selected = this.groups[0];
      this.emitSelected();
    }
  }

  public selectById(id: string) {
    const group = this.groups.find((group: ComparisonGroup) => group.id == id);
    if (group) {
      this.select(group);
    }
  }

  protected async load() {
    this.loading = true;
    this.groups = await this.getComparisonGroups(REQUEST_TAG);
    this.sortGroups();
    this.loading = false;
    this.$emit("ready", true);
  }

  protected async create() {
    if (!this.current.name) {
      return;
    }

    const group = await this.createComparisonGroup(this.current, REQUEST_TAG);

    this.groups.push(group);
    this.sortGroups();

    this.stopCreating();

    this.selected = group;
    this.$nextTick(() => {
      this.scrollToSelected();
    });

    this.emitSelected();
  }

  protected canAdd(value: Company | Brand | Product): boolean {
    if (!this.selected) {
      return false;
    }

    return this.selected.hasEntry(value) === false;
  }

  protected async addEntry(value: Company | Brand | Product) {
    if (!this.selected) {
      return;
    }

    this.selected.addEntry(value);

    try {
      this.loading = true;
      await this.addToComparisonGroup(this.selected, value, REQUEST_TAG);
      this.loading = false;
      this.emitSelected();
    } catch (e) {
      this.selected.removeEntry(value);
    }
  }

  protected async removeEntry(value: Company | Brand | Product) {
    if (!this.selected) {
      return;
    }

    this.selected.removeEntry(value);

    try {
      this.loading = true;
      await this.removeFromComparisonGroup(this.selected, value, REQUEST_TAG);
      this.loading = false;
      this.emitSelected();
    } catch (e) {
      this.selected.addEntry(value);
    }
  }

  protected scrollToSelected() {
    if (this.selected === null) {
      return;
    }

    const container = this.$refs.list as HTMLElement;
    const selected = this.$refs[`item-${this.selected.id}`] as Element[];

    if (selected.length == 0) {
      return;
    }

    const element = selected[0] as HTMLElement;
    if (element.offsetTop > container.clientHeight) {
      container.scrollTop =
        element.offsetTop + element.clientHeight - container.clientHeight;
    } else if (element.offsetTop < container.scrollTop) {
      container.scrollTop = element.offsetTop;
    }
  }

  protected sortGroups() {
    this.groups.sort(
      (first: ComparisonGroup, second: ComparisonGroup): number => {
        if (first.name.toUpperCase() < second.name.toUpperCase()) {
          return -1;
        } else if (first.name.toUpperCase() > second.name.toUpperCase()) {
          return 1;
        }

        return 0;
      }
    );
  }

  protected clearSearchResult() {
    this.searchResult.companies = [];
    this.searchResult.brands = [];
    this.searchResult.products = [];
    this.searchResult.total = 0;
  }

  protected emitSelected() {
    this.$emit("selected", this.selected);
  }

  protected onSearch(results: EntrySearchResult) {
    this.searchResult = results;
  }
}
